import { Check } from "lucide-react";
import { Cta } from "@/components/public/PublicChrome";
import { Reveal } from "@/components/public/Reveal";
import { H2 } from "@/components/public/PageParts";
import { usePlanConfig, PlanTable } from "@/components/PlanInfo";
import { TIERS, PASSES, PLAN_DETAILS } from "@/lib/pricing";

const CARD = "rounded-[1.5rem] border p-6 sm:p-7 flex flex-col h-full";

function TierCard({ t }) {
  return (
    <div className={`${CARD} ${t.popular ? "border-sky-400/50 bg-sky-400/[0.06] shadow-[0_30px_80px_-40px_rgba(37,99,255,0.8)]" : "border-white/[0.08] bg-[#10141F] hover:border-white/20"}`} data-testid={`pricing-card-${t.id}`}>
      <div className="flex items-center justify-between gap-3">
        <h2 className="font-display text-xl font-semibold">{t.name}</h2>
        {t.popular && <span className="text-[10px] uppercase tracking-wider rounded-full bg-sky-400 text-white px-2 py-0.5" data-testid={`pricing-popular-${t.id}`}>Most popular</span>}
      </div>
      <p className="text-sm text-neutral-400 mt-1">{t.tag}</p>
      <p className="mt-6" data-testid={`pricing-price-${t.id}`}><span className="font-display text-5xl font-semibold tracking-tight">${t.m}</span><span className="text-neutral-500 text-sm">/mo</span></p>
      <p className="text-xs text-neutral-500 mt-1.5">{t.y ? `or $${t.y} a year` : "Free forever"}</p>
      <ul className="mt-6 pt-6 border-t border-white/[0.07] space-y-2.5 text-sm text-neutral-300 flex-1">
        {t.feats.map((f) => (f.endsWith("plus:")
          ? <li key={f} className="text-neutral-400">{f}</li>
          : <li key={f} className="flex gap-2.5"><Check className="h-4 w-4 text-sky-300 shrink-0 mt-0.5" aria-hidden="true" />{f}</li>))}
      </ul>
      <Cta testid={`pricing-cta-${t.id}`} className="mt-7 w-full">{t.m ? `Upgrade to ${t.name}` : "Create free account"}</Cta>
    </div>
  );
}

export function TierGrid() {
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
      {TIERS.map((t, i) => (
        <Reveal key={t.name} delay={i * 70} className={t.popular ? "order-first md:order-none" : ""}><TierCard t={t} /></Reveal>
      ))}
    </div>
  );
}

export function Passes() {
  return (
    <section className="mt-24 sm:mt-32" aria-labelledby="passes-title">
      <Reveal><H2 id="passes-title">Passes</H2></Reveal>
      <Reveal delay={80} className="mt-8 grid sm:grid-cols-3 gap-px overflow-hidden rounded-3xl border border-white/[0.07] bg-white/[0.07]">
        {PASSES.map((p) => (
          <div key={p.key} className="bg-[#0F1320] p-6 sm:p-7" data-testid={`pricing-pass-${p.key}`}>
            <p className="font-medium">{p.name}</p>
            <p className="font-display text-3xl font-semibold tracking-tight mt-3">${p.price}</p>
            <p className="text-xs text-neutral-500 mt-2 leading-relaxed">{p.desc}</p>
          </div>
        ))}
      </Reveal>
    </section>
  );
}

export function PricingDetails() {
  const cfg = usePlanConfig();
  return (
    <section className="mt-24 sm:mt-32 space-y-6" aria-labelledby="plan-details-title" data-testid="pricing-plan-details">
      <Reveal><H2 id="plan-details-title">Plan details</H2></Reveal>
      <Reveal delay={80} className="space-y-6">
        <PlanTable cfg={cfg} testid="pricing-plan-table" />
        <ul className="text-xs text-neutral-400 space-y-2 list-disc pl-4 max-w-3xl leading-relaxed">{PLAN_DETAILS.map((d) => <li key={d}>{d}</li>)}</ul>
      </Reveal>
    </section>
  );
}
